// Women's Circles - Core Page (Using client's exact wording)
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Calendar } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, SchemaScript } from "../components/shared/SEOHelper";
import { ReviewsStrip } from "../components/shared/Reviews";
import { BookwhenEmbed } from "../components/shared/BookwhenEmbed";

const WomensCirclesPage = () => {
  usePageMeta({
    title: "Women's Circles | A Space to Pause, Share & Reconnect",
    description: "Women's circles in Frome and Devon. A space to step out of the busyness of everyday life and come back to yourself.",
    canonical: "https://annelielily.com/womens-circles"
  });

  const serviceSchema = generateServiceSchema({
    name: "Women's Circles",
    description: "Women's circles in Frome and Devon. A space to pause, share and reconnect with yourself and other women.",
    serviceType: "WellnessService",
    url: "https://annelielily.com/womens-circles"
  });

  // What to expect items from client's email
  const whatToExpect = [
    "Grounding practices or meditation",
    "Breathwork or gentle movement",
    "Journaling or reflection",
    "Group sharing (optional)",
    "Seasonal themes or intention setting",
    "Working with the chakras, the elements, the moon cycles, astrology and nature"
  ];

  // Why join items from client's email
  const whyJoin = [
    "Overwhelmed or holding a lot on their own",
    "Disconnected from themselves or others",
    "In need of space to pause and reflect",
    "Drawn to something deeper but unsure where to begin",
    "Like they want to be part of a community"
  ];

  // What women notice from client's email
  const whatWomenNotice = [
    "A deeper connection to themselves",
    "More clarity and awareness",
    "A sense of support and belonging",
    "Meaningful connections with others"
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Women's Circles" currentSlug="womens-circles" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Women's Circles
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              A Space to Pause, Share & Reconnect
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              Women's circles offer a space to step out of the busyness of everyday life and come back to yourself. 
              A space to slow down, to be heard, and to connect with others in a way that often feels missing in modern life.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Calendar className="w-4 h-4 text-gold" />
                Monthly gatherings
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                Frome &amp; Devon
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="womens-circles-book-btn"
              >
                Book Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="btn-secondary"
                data-testid="womens-circles-enquire-btn"
              >
                Ask a Question
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Book a Women's Circle - Bookwhen (top placement, same calendar as classes) */}
      <BookwhenEmbed
        slug="annelielily"
        title="Book a Women's Circle"
        subtitle="See upcoming circles below and reserve your place in seconds."
        bgClass="bg-cream"
        testId="womens-circles-bookwhen"
      />

      {/* What is a Women's Circle - consolidated per client copy */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
                What is a Women's Circle
              </h2>
              <div className="prose-content space-y-4 text-neutral-700">
                <p>
                  A women's circle is a gathering where women come together in a spirit of openness and support — to slow down, reflect and share, with no pressure to do anything other than simply be.
                </p>
                <p>
                  I bring women together in circle as one of the most deeply healing experiences you can offer yourself, held within the warm, nurturing loft room of a beautiful Grade II listed cottage on the outskirts of Frome.
                </p>
                <p>
                  These circles are open to all women. You do not need any previous experience.
                </p>
                <p className="italic text-neutral-600">
                  Whether this is your first time or something you have been part of before, you are very welcome.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/s4dqh7cg_IMG_1640.jpg"
                  alt="Annelie Lily leading a women's circle ceremony with sacred altar, crystals, candles and oracle cards in a sunlit space"
                  className="w-full h-full object-contain bg-cream"
                  width={600}
                  height={600}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What to Expect
            </h2>
            <p className="text-neutral-700 mb-6">
              Each circle is gently guided and may include:
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {whatToExpect.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3 bg-white/70 p-4 rounded-xl border border-gold/10"
                >
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-neutral-600 mt-6 italic">
              Sometimes we may include simple ceremonies, such as working with the seasons, the moon, or a cacao ceremony.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Join a Circle */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
                Why Join a Circle
              </h2>
              <p className="text-neutral-700 mb-6">
                Many women come because they are feeling:
              </p>
              <ul className="space-y-3 mb-8">
                {whyJoin.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-gold mt-2 flex-shrink-0" />
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-neutral-700 bg-gold/5 p-5 rounded-xl border border-gold/10 italic">
                Within the circle, you will be heard, you no longer need to hold onto everything alone, 
                and you can reconnect with yourself.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/w7ox3edj_IMG_5226.jpg"
                  alt="Women seated together in a circle around a colourful chakra mandala spread with candles, oracle cards and reflection journals in a warm sunlit studio"
                  className="w-full h-full object-cover"
                  width={600}
                  height={600}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Experience */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/7obort24_IMG_2796.jpg"
                  alt="Women's circle gathered around an outdoor stone fire pit at sunset, with mountains glowing orange in the distance and women standing in a circle holding space together"
                  className="w-full h-full object-cover"
                  width={600}
                  height={600}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
                The Experience
              </h2>
              <div className="prose-content space-y-4 text-neutral-700 mb-8">
                <p>
                  <strong>There is no expectation to share.</strong>
                </p>
                <p>
                  There is no need to "have something to say".
                </p>
                <p>
                  You can come quietly, listen, and take what you need from the space.
                </p>
              </div>

              <div className="bg-white/70 p-6 rounded-xl border border-gold/10">
                <p className="text-neutral-700 mb-4">
                  Over time, many women notice:
                </p>
                <ul className="space-y-3">
                  {whatWomenNotice.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-neutral-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-neutral-800 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-4">
              Book a Women's Circle
            </h2>
            <p className="text-neutral-300 mb-8 max-w-xl mx-auto">
              Please click the link below to book your place. 
              If you have any questions, feel free to get in touch.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/womens-circles#book" 
                className="bg-gold hover:bg-gold-dark text-white px-8 py-3.5 rounded-full font-medium transition-colors inline-flex items-center justify-center gap-2"
              >
                Book a Circle <ArrowRight className="w-4 h-4" />
              </Link>
              <a 
                href="tel:07540995021" 
                className="border border-white/30 hover:border-white text-white px-8 py-3.5 rounded-full font-medium transition-colors"
              >
                Call 07540 995021
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsStrip pageType="womens-circles" title="What Our Circle Members Say" />

      {/* Internal Links for SEO */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <p className="text-neutral-500 text-sm text-center mb-4">
            Explore more ways to work with Annelie:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/yoga-classes" className="text-gold hover:underline text-sm">Yoga Classes</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/treatments" className="text-gold hover:underline text-sm">Healing & Treatments</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/retreats" className="text-gold hover:underline text-sm">Retreats</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WomensCirclesPage;
